import axios from "axios";
import { CalendarIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FaBuilding,
  FaUser,
  FaBriefcase,
  FaMoneyBillWave,
  FaFileSignature,
  FaUpload,
  FaCalendarDays,
  FaXmark,
  FaClipboardCheck,
} from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../payslip/payslippicker.css";
const steps = [
  {
    id: 1,
    title: "Company\nInformation",
    mobileTitle: "Company\nInformation",
    icon: FaBuilding,
    fields: [
      "companyName",
      "companyEmail",
      "companyPhone",
      "companyWebsite",
      "companyAddress",
      "companyLogo",
    ],
  },
  {
    id: 2,
    title: "Employee\nInformation",
    mobileTitle: "Employee\nInformation",
    icon: FaUser,
    fields: [
      "employeeName",
      "employeeId",
      "employeePhone",
      "employeeEmail",
      "residentialAddress",
    ],
  },
  {
    id: 3,
    title: "Employment\nInformation",
    mobileTitle: "Employment\nInformation",
    icon: FaBriefcase,
    fields: [
      "finalDesignation",
      "department",
      "employmentType",
      "joiningDate",
      "workLocation",
      "lastWorkingDate",
      "reportingManager",
    ],
  },
  {
    id: 4,
    title: "Experience &\nPerformance Details",
    mobileTitle: "Experience &\nPerformance Details",
    icon: FaClipboardCheck,
    fields: [
      "keyResponsibilities",
      "skillsContribution",
      "performanceStatement",
      "conductStatement",
      "reasonForLeaving",
      "achievementsRemarks",
    ],
  },
  {
    id: 5,
    title: "Letter\nInformation",
    mobileTitle: "Letter\nInformation",
    icon: FaFileSignature,
    fields: [
      "experienceLetterDate",
      "placeOfIssue",
      "referenceNumber",
      "signatoryName",
      "signatoryDesignation",
    ],
  },
];

const defaultValues = {
  // Company
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  companyAddress: "",
  companyLogo: null,

  // Employee
  employeeName: "",
  employeeId: "",
  employeePhone: "",
  employeeEmail: "",
  residentialAddress: "",

  // Employment
  finalDesignation: "",
  department: "",
  employmentType: "",
  joiningDate: "",
  workLocation: "",
  lastWorkingDate: "",
  reportingManager: "",

  // Experience
  keyResponsibilities: "",
  skillsContribution: "",
  performanceStatement: "",
  conductStatement: "",
  reasonForLeaving: "",
  achievementsRemarks: "",

  // Letter
  experienceLetterDate: "",
  placeOfIssue: "",
  referenceNumber: "",
  signatoryName: "",
  signatoryDesignation: "",
};

const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0";

const labelClass = "mb-1.5 block text-sm font-medium text-[#10324F]";

const FormField = ({ label, required, error, children }) => {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-[10px] text-red-500">{error.message}</p>
      )}
    </div>
  );
};
const BASE_URL = import.meta.env.VITE_BASE_URL;

const RelievingLetterGeneratorForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const values = watch();

  const currentStep = steps[activeStep];

  const handleNext = async () => {
    const valid = await trigger(currentStep.fields);

    // Company logo is manually handled
    if (activeStep === 0 && !logoPreview) {
      setError("companyLogo", {
        type: "required",
        message: "Company logo is required",
      });

      return;
    }

    if (!valid) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    clearErrors("companyLogo");

    // Revalidate the field
    //  await trigger("companyLogo");
  };
  const handleLogoChangedd = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
  };
  const onSubmit = async (data) => {
    try {
      if (isGenerating) return;

      setIsGenerating(true);

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "companyLogo") {
          if (value instanceof File) {
            formData.append("companyLogo", value);
          }
        } else {
          formData.append(key, value ?? "");
        }
      });

      const response = await axios.post(
        `${BASE_URL}/generate-experienceletter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      const contentType = response.headers["content-type"];

      if (!contentType?.includes("application/pdf")) {
        const text = await response.data.text();

        console.error("Backend returned:", text);

        throw new Error("Backend did not return an Experience Letter PDF");
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Experience-Letter-${data.employeeName || "Employee"}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.open(url, "_blank");

      reset(defaultValues);

      setLogoPreview("");

      setActiveStep(0);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("Experience letter generation failed:", error);

      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();

        try {
          const errorData = JSON.parse(text);

          alert(errorData?.message || "Failed to generate experience letter");
        } catch {
          alert("Failed to generate experience letter");
        }
      } else {
        alert(error?.message || "Failed to generate experience letter");
      }
    } finally {
      setIsGenerating(false);
    }
  };
  const clearLogo = () => {
    setLogoPreview("");
    setValue("companyLogo", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  return (
    <section>
      <form className="w-full">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(300px,0.9fr)]">
          {/* ================= FORM ================= bg-[#eef4f8]*/}
          <div className="min-w-0 rounded-xl border border-white  shadow-sm backdrop-blur-2xl p-4">
            {/* STEP NAVIGATION */}
            <div className="scrollbar-hide flex w-full gap-2 overflow-x-auto pb-2 sm:overflow-visible">
              {steps.map((step, index) => {
                const active = activeStep === index;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={async () => {
                      if (index <= activeStep) {
                        setActiveStep(index);
                        return;
                      }

                      const valid = await trigger(steps[activeStep].fields);

                      if (valid) {
                        setActiveStep(index);
                      }
                    }}
                    className={`flex min-w-26.25 shrink-0 flex-1 items-center justify-center rounded-md px-2 py-2.5 text-center transition
          sm:min-w-0 
          ${
            active
              ? " bg-[#10324F] text-white shadow-sm"
              : "border border-white  text-[#10324F] bg-linear-to-b from-white/60 to-[#D1DFEA]/30  shadow-sm hover:bg-white/70 "
          }`}
                  >
                    <span className="whitespace-pre-line text-xs leading-4 sm:text-sm">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CURRENT STEP */}
            <div className="mt-5">
              {activeStep === 0 && (
                <CompanyInformation
                  register={register}
                  errors={errors}
                  handleLogoChange={handleLogoChange}
                  logoPreview={logoPreview}
                  clearLogo={clearLogo}
                />
              )}

              {activeStep === 1 && (
                <EmployeeInformation register={register} errors={errors} />
              )}

              {activeStep === 2 && (
                <EmploymentInformation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 3 && (
                <ExperiencePerformanceDetails
                  register={register}
                  errors={errors}
                />
              )}

              {activeStep === 4 && (
                <LetterInformation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}
            </div>

            {/* NAVIGATION BUTTONS */}
            <div className="mt-7 flex items-center justify-center gap-3">
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-30 rounded-md border border-[#163957] px-6 py-2.5 text-sm font-medium text-[#163957] transition hover:bg-[#163957] hover:text-white"
                >
                  Previous
                </button>
              )}

              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-30 rounded-md bg-[#163957] px-8 py-2.5 text-sm font-medium text-white transition hover:bg-[#10324F]"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleSubmit(onSubmit)}
                  className={`min-w-45 rounded-md px-8 py-2.5 text-sm font-medium text-white transition ${
                    isGenerating
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-[#10324F] hover:bg-[#10324F]"
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </span>
                  ) : (
                    "Generate Experience Letter"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ================= PREVIEW ================= */}
          <ExperienceLetterPreview values={values} logoPreview={logoPreview} />
        </div>
      </form>
    </section>
  );
};

export default RelievingLetterGeneratorForm;
const CompanyInformation = ({
  register,
  errors,
  handleLogoChange,
  logoPreview,
  clearLogo,
}) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Company Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Company Name" required error={errors.companyName}>
          <input
            {...register("companyName", {
              required: "Company name is required",
              minLength: {
                value: 2,
                message: "Enter a valid company name",
              },
            })}
            className={inputClass}
            placeholder="Enter Company name"
          />
        </FormField>

        <FormField label="Company Email" required error={errors.companyEmail}>
          <input
            type="email"
            {...register("companyEmail", {
              required: "Company email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Email"
          />
        </FormField>

        <FormField label="Phone Number" required error={errors.companyPhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("companyPhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Website" error={errors.companyWebsite}>
          <input
            {...register("companyWebsite", {
              required: "Company website is required",
              pattern: {
                value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
                message: "Enter valid website",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Website"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Company Address"
            required
            error={errors.companyAddress}
          >
            <input
              {...register("companyAddress", {
                required: "Company address is required",
              })}
              className={inputClass}
              placeholder="Enter Company Address"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField label="Company Logo" required error={errors.companyLogo}>
            {logoPreview ? (
              <div className="w-60">
                <div className="relative flex h-16 w-full items-center justify-center rounded-md border border-slate-200 bg-white">
                  <img
                    src={logoPreview}
                    alt="Company logo"
                    className="h-14 max-w-55 object-contain"
                  />

                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
                    aria-label="Remove logo"
                  >
                    <FaXmark className="text-xs" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex h-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white transition hover:bg-slate-50">
                <div className="text-center text-[10px] text-[#10324F]">
                  <FaUpload className="mx-auto mb-1 text-[#10324F]/30" />
                  Upload Logo
                </div>

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </FormField>
        </div>
      </div>
    </div>
  );
};

const EmployeeInformation = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Employee Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <FormField
            label="Employee Full Name"
            required
            error={errors.employeeName}
          >
            <input
              {...register("employeeName", {
                required: "Employee name is required",
              })}
              className={inputClass}
              placeholder="Enter employee name"
            />
          </FormField>
        </div>

        <FormField label="Employee ID" error={errors.employeeId}>
          <input
            {...register("employeeId")}
            className={inputClass}
            placeholder="Enter employee ID"
          />
        </FormField>

        <FormField label="Phone Number" required error={errors.employeePhone}>
          <input
            {...register("employeePhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            type="tel"
            inputMode="numeric"
            maxLength={10}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>
        <div className="sm:col-span-2">
          <FormField label="Email" required error={errors.employeeEmail}>
            <input
              type="email"
              {...register("employeeEmail", {
                required: "Email is required",
                pattern: {
                  value:
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                  message: "Enter valid email",
                },
              })}
              className={inputClass}
              placeholder="Enter Email"
            />
          </FormField>
        </div>
        <div className="sm:col-span-2">
          <FormField
            label="Residential Address"
            required
            error={errors.residentialAddress}
          >
            <input
              {...register("residentialAddress", {
                required: "Address is required",
              })}
              className={inputClass}
              placeholder="Enter Residential Address"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};
const EmploymentInformation = ({ register, errors, control }) => {
  const parseDateValue = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value).split("-").map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const formatDateValue = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Employment Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Final designation"
          required
          error={errors.finalDesignation}
        >
          <input
            {...register("finalDesignation", {
              required: "Final designation is required",
            })}
            className={inputClass}
            placeholder="Enter final designation"
          />
        </FormField>

        <FormField label="Department" required error={errors.department}>
          <input
            {...register("department", {
              required: "Department is required",
            })}
            className={inputClass}
            placeholder="Enter Department"
          />
        </FormField>

        <FormField
          label="Employment type"
          required
          error={errors.employmentType}
        >
          <select
            {...register("employmentType", {
              required: "Employment type is required",
            })}
            className={inputClass}
          >
            <option value="">Select Type</option>
            <option value="Full Time">Full Time</option>
            <option value="Part Time">Part Time</option>
            <option value="Contract">Contract</option>
            <option value="Internship">Internship</option>
            <option value="Temporary">Temporary</option>
          </select>
        </FormField>

        <FormField label="Date of joining" required error={errors.joiningDate}>
          <Controller
            name="joiningDate"
            control={control}
            rules={{
              required: "Joining date is required",
            }}
            render={({ field, fieldState }) => (
              <div className="relative joining-date-picker">
                <DatePicker
                  selected={parseDateValue(field.value)}
                  onChange={(date) => {
                    field.onChange(formatDateValue(date));
                  }}
                  onBlur={field.onBlur}
                  dateFormat="yyyy-MM-dd"
                  showPopperArrow={false}
                  isClearable
                  placeholderText="Select date"
                  showIcon
                  toggleCalendarOnIconClick
                  icon={<CalendarIcon className="h-4 w-4" />}
                  className={`${inputClass} h-10`}
                  wrapperClassName="w-full"
                  popperClassName="payslip-datepicker-popper react-popper-offset"
                  calendarClassName="payslip-datepicker"
                  popperPlacement="top-start"
                  portalId="datepicker-portal"
                />

                {fieldState.error && (
                  <p className="mt-1 text-[10px] text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField label="Work Location" required error={errors.workLocation}>
            <input
              {...register("workLocation", {
                required: "Work location is required",
              })}
              className={inputClass}
              placeholder="Enter Location"
            />
          </FormField>
        </div>

        <FormField
          label="Last working date"
          required
          error={errors.lastWorkingDate}
        >
          <Controller
            name="lastWorkingDate"
            control={control}
            rules={{
              required: "Last working date is required",
            }}
            render={({ field, fieldState }) => (
              <div className="relative joining-date-picker">
                <DatePicker
                  selected={parseDateValue(field.value)}
                  onChange={(date) => {
                    field.onChange(formatDateValue(date));
                  }}
                  onBlur={field.onBlur}
                  dateFormat="yyyy-MM-dd"
                  showPopperArrow={false}
                  isClearable
                  placeholderText="Select date"
                  showIcon
                  toggleCalendarOnIconClick
                  icon={<CalendarIcon className="h-4 w-4" />}
                  className={`${inputClass} h-10`}
                  wrapperClassName="w-full"
                  popperClassName="payslip-datepicker-popper react-popper-offset"
                  calendarClassName="payslip-datepicker"
                  popperPlacement="top-start"
                  portalId="datepicker-portal"
                />

                {fieldState.error && (
                  <p className="mt-1 text-[10px] text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FormField>

        <FormField label="Reporting manager" error={errors.reportingManager}>
          <input
            {...register("reportingManager")}
            className={inputClass}
            placeholder="Enter reporting manager"
          />
        </FormField>
      </div>
    </div>
  );
};

const ExperiencePerformanceDetails = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Experience and Performance Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Key responsibilities"
          required
          error={errors.keyResponsibilities}
        >
          <input
            {...register("keyResponsibilities", {
              required: "Key responsibilities are required",
            })}
            className={inputClass}
            placeholder="Enter key responsibilities"
          />
        </FormField>

        <FormField
          label="Skills and areas of contribution"
          required
          error={errors.skillsContribution}
        >
          <input
            {...register("skillsContribution", {
              required: "Skills and contribution are required",
            })}
            className={inputClass}
            placeholder="Enter skills and areas of contribution"
          />
        </FormField>

        <FormField
          label="Performance statement"
          required
          error={errors.performanceStatement}
        >
          <input
            {...register("performanceStatement", {
              required: "Performance statement is required",
            })}
            className={inputClass}
            placeholder="Enter performance statement"
          />
        </FormField>

        <FormField
          label="Conduct statement"
          required
          error={errors.conductStatement}
        >
          <input
            {...register("conductStatement", {
              required: "Conduct statement is required",
            })}
            className={inputClass}
            placeholder="Enter conduct statement"
          />
        </FormField>

        <FormField label="Reason for leaving" error={errors.reasonForLeaving}>
          <select {...register("reasonForLeaving")} className={inputClass}>
            <option value="">Select reason for leaving</option>
            <option value="Resignation">Resignation</option>
            <option value="Career Growth">Career Growth</option>
            <option value="Higher Studies">Higher Studies</option>
            <option value="Personal Reasons">Personal Reasons</option>
            <option value="End of Contract">End of Contract</option>
            <option value="Relocation">Relocation</option>
            <option value="Other">Other</option>
          </select>
        </FormField>

        <FormField
          label="Achievements or additional remarks"
          error={errors.achievementsRemarks}
        >
          <input
            {...register("achievementsRemarks")}
            className={inputClass}
            placeholder="Enter achievements or additional remarks"
          />
        </FormField>
      </div>
    </div>
  );
};

const LetterInformation = ({ register, errors, control }) => {
  const parseDateValue = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value).split("-").map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const formatDateValue = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Letter Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Experience letter date"
          required
          error={errors.experienceLetterDate}
        >
          <Controller
            name="experienceLetterDate"
            control={control}
            rules={{
              required: "Experience letter date is required",
            }}
            render={({ field, fieldState }) => (
              <div className="relative joining-date-picker">
                <DatePicker
                  selected={parseDateValue(field.value)}
                  onChange={(date) => {
                    field.onChange(formatDateValue(date));
                  }}
                  onBlur={field.onBlur}
                  dateFormat="yyyy-MM-dd"
                  showPopperArrow={false}
                  isClearable
                  placeholderText="Select letter date"
                  showIcon
                  toggleCalendarOnIconClick
                  icon={<CalendarIcon className="h-4 w-4" />}
                  className={`${inputClass} h-10`}
                  wrapperClassName="w-full"
                  popperClassName="payslip-datepicker-popper react-popper-offset"
                  calendarClassName="payslip-datepicker"
                  popperPlacement="top-start"
                  portalId="datepicker-portal"
                />

                {fieldState.error && (
                  <p className="mt-1 text-[10px] text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FormField>

        <FormField label="Place of issue" required error={errors.placeOfIssue}>
          <input
            {...register("placeOfIssue", {
              required: "Place of issue is required",
            })}
            className={inputClass}
            placeholder="Enter Place of issue"
          />
        </FormField>

        <FormField label="Reference number" error={errors.referenceNumber}>
          <input
            {...register("referenceNumber")}
            className={inputClass}
            placeholder="Enter Reference number"
          />
        </FormField>

        <FormField
          label="Authorised signatory name"
          required
          error={errors.signatoryName}
        >
          <input
            {...register("signatoryName", {
              required: "Signatory name is required",
            })}
            className={inputClass}
            placeholder="Enter signatory name"
          />
        </FormField>
        <div className="sm:col-span-2">
          <FormField
            label="Authorised signatory designation"
            required
            error={errors.signatoryDesignation}
          >
            <input
              {...register("signatoryDesignation", {
                required: "Designation is required",
              })}
              className={inputClass}
              placeholder="Enter signatory designation"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const ExperienceLetterPreview = ({ values, logoPreview }) => {
  const formatDate = (date) => {
    if (!date) return "24 August, 2026";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return date;

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-xl border border-white shadow-sm backdrop-blur-2xl p-4">
      <h2 className="mb-5 text-sm font-semibold text-[#10324F]">Preview</h2>

      <div className="mx-auto w-full  bg-white">
        <Ribbon position="top" />

        <div className="px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company"
                className="h-8 max-w-25 object-contain"
              />
            ) : (
              <span className="text-lg text-[#10324F]">
                {values.companyName || "Company"}
              </span>
            )}

            {/* <div className="text-right">
              <p className="text-xs font-bold text-[#10324F]">
                EXPERIENCE LETTER
              </p>

              <p className="mt-1 text-xs text-[#10324F]">
                {formatDate(values.experienceLetterDate)}
              </p>
            </div> */}
          </div>
          {/* Heading */}
          <div className="mt-6 text-center">
            <p className="text-xs font-bold text-[#10324F]">
              EXPERIENCE LETTER
            </p>
          </div>

          {/* Reference */}
          {values.referenceNumber && (
            <div className="mt-5 text-xs text-[#10324F]">
              <p>
                <strong>Reference No:</strong> {values.referenceNumber}
              </p>
            </div>
          )}

          {/* Recipient */}
          <div className="mt-7 text-xs leading-4 text-[#10324F]">
            <p className="font-semibold">To:</p>

            <p>{values.employeeName || "Employee Name"}</p>

            <p>{values.residentialAddress || "Employee Address"}</p>
          </div>

          {/* Body */}
          <div className="mt-6 text-xs leading-4 text-[#10324F]">
            <p>
              This is to certify that{" "}
              <strong>{values.employeeName || "Employee Name"}</strong>,
              Employee ID <strong>{values.employeeId || "Employee ID"}</strong>,
              was employed with{" "}
              <strong>{values.companyName || "Company Name"}</strong> as{" "}
              <strong>{values.finalDesignation || "Designation"}</strong> in the{" "}
              <strong>{values.department || "Department"}</strong> department.
            </p>

            <p className="mt-3">
              During their tenure with the organization, they worked from{" "}
              <strong>{formatDate(values.joiningDate)}</strong> to{" "}
              <strong>{formatDate(values.lastWorkingDate)}</strong>.
            </p>

            {values.keyResponsibilities && (
              <p className="mt-3">
                Their key responsibilities included {values.keyResponsibilities}
                .
              </p>
            )}

            {values.skillsContribution && (
              <p className="mt-3">
                They demonstrated skills and contributed in the following areas:{" "}
                {values.skillsContribution}.
              </p>
            )}

            {values.performanceStatement && (
              <p className="mt-3">Performance: {values.performanceStatement}</p>
            )}

            {values.conductStatement && (
              <p className="mt-3">Conduct: {values.conductStatement}</p>
            )}

            {values.achievementsRemarks && (
              <p className="mt-3">
                Additional remarks: {values.achievementsRemarks}
              </p>
            )}

            {values.reasonForLeaving && (
              <p className="mt-3">
                Reason for leaving: {values.reasonForLeaving}
              </p>
            )}

            <p className="mt-3">
              We appreciate their contributions to the organization and wish
              them success in all their future endeavors.
            </p>
          </div>

          {/* Place/date */}
          <div className="mt-6 text-xs text-[#10324F]">
            <p>Place of Issue: {values.placeOfIssue || "__________"}</p>

            <p className="mt-1">
              Date: {formatDate(values.experienceLetterDate)}
            </p>
          </div>

          {/* Signature */}
          <div className="mt-7 text-xs text-[#10324F]">
            <p>Sincerely,</p>

            <p className="mt-4 font-semibold">
              {values.signatoryName || "Authorised Signatory"}
            </p>

            <p>{values.signatoryDesignation || "HR Manager"}</p>

            <p>{values.companyName || "Company Name"}</p>
          </div>

          <div className="mt-8 text-center text-xs text-[#10324F]">
            Powered by Waggex
          </div>
        </div>

        <Ribbon position="bottom" />
      </div>
    </div>
  );
};
const Ribbon = ({ position = "top" }) => {
  const isTop = position === "top";

  return (
    <div className="relative -mx-px h-9.25 w-[calc(100%+2px)] overflow-hidden">
      {/* Green base */}
      <div className="absolute -inset-0.5 bg-[#10324F]" />

      <div
        className="absolute -inset-0.5"
        style={{
          transform: isTop ? "none" : "scaleX(-1) scaleY(-1)",
        }}
      >
        {/* Magenta ribbon */}
        <div
          className="absolute inset-0 bg-[#D1DFEA]"
          style={{
            clipPath:
              "polygon(76% 0%, 100% 0%, 100% 100%, 51% 100%, 47% 60%, 70% 55%)",
          }}
        />

        {/* White cut / separation */}
        <div
          className="absolute bottom-0 left-0 h-4 w-[55%] bg-white"
          style={{
            clipPath: "polygon(0 0, 88% 0, 100% 100%, 0 100%)",
          }}
        />
      </div>
    </div>
  );
};
